/**
 * "Categoría de interés" (US 2203, generalized to `map_accuracy_category` by
 * US 2202) — a per-model categorical row filter available to a small
 * allow-list of charts (`supportsCategoryOfInterest`). Pick one column, pick
 * which of its distinct values are of interest, and the selection is
 * auto-maintained as an OR-combined categorical filter on EACH model (LP and
 * CP each read their own column position), composed with AND into that
 * model's existing filter selector via `withAutoCombined`.
 *
 * The generated cards use the reserved `m{modelIndex}period{n}` / `…periodOr`
 * id convention (filterSpec.ts) so they never collide with, and stay
 * invisible to, the general per-model filter cards the user edits by hand
 * (FilterPanel hides them from its card list). Everything in this module is
 * generic over `modelIndex` alone — no algorithm ever gates the mechanism
 * itself, only whether `AlgorithmCard` renders the picker at all.
 */

import type { CategoricalFilter, ContinuousFilter, FilterSpec } from '../types/compute';
import { isTonnageAlgorithm } from '../config/measures';
import type { FileColumn } from './fileMetadata';
import { isPeriodLeafId, nextPeriodLeafId, periodOrFilterId, withAutoCombined } from './filterSpec';

/** Algorithms whose chart offers the "categoría de interés" picker (US 2202
 *  §1) — an explicit allow-list, not "every algorithm": a per-model row
 *  filter doesn't obviously make sense for a single-model distribution chart
 *  (`histogram`, `scatter`) or a spatial map keyed by a fixed cut-off
 *  (`map_accuracy_cutoff`), so each chart opts in deliberately rather than
 *  gaining it as a side effect of some other change. `map_accuracy_category`
 *  shares its `spatial-map` layout/builder with `map_accuracy_cutoff` but is
 *  the one gated in, since it already keys its class decision on a picked
 *  category rather than a numeric cut-off. */
export function supportsCategoryOfInterest(name: string): boolean {
  return isTonnageAlgorithm(name) || name === 'map_accuracy_category';
}

/**
 * A period leaf as this module ever WRITES it (`togglePeriod`) is always
 * `categorical`. But `batchRequestBuilder`'s `lowerModelFilters` runs every
 * card through `numericCategoricalToContinuous` (SPEC/2233 — the Lib any_casts
 * a numeric categorical value's stored cell as a `double`, not a `string`, so
 * a value like "6" has to travel as a single-point `continuous [6, 6]` filter
 * instead), preserving the id but swapping the type. A period column of
 * numeric-looking values (a "mes" column: 6, 7, 8…) is exactly this case, so
 * reading a period leaf back — from an executed result, via `liftModelFilters`
 * — must recognize BOTH shapes or it silently sees no period filter at all.
 */
type PeriodLeaf = CategoricalFilter | ContinuousFilter;

/** This model's period filter cards, in insertion order — `categorical` or the
 *  numeric-value `continuous` shape they may have been lowered into (see
 *  `PeriodLeaf` above). */
export function periodLeaves(filters: FilterSpec[] | undefined, modelIndex: number): PeriodLeaf[] {
  return (filters ?? []).filter(
    (f): f is PeriodLeaf =>
      (f.type === 'categorical' || f.type === 'continuous') && isPeriodLeafId(f.id, modelIndex)
  );
}

/** This leaf's column position, whichever shape it's in. */
function periodLeafColumn(f: PeriodLeaf): number {
  return f.type === 'categorical' ? f.category_column : f.continuous_column;
}

/** Numeric-looking values collapse to `Number(v)`'s canonical string ("08" →
 *  "8"), same test `numericCategoricalToContinuous` uses to decide whether to
 *  lower at all. Neither `ContinuousFilter` nor the leaf id carries the
 *  original string, so once a value round-trips through that lowering its
 *  formatting is gone for good — canonicalizing on BOTH the option a user
 *  picks from (`periodValueOptions`) and the value read back off a leaf
 *  (below), and the value handed to `togglePeriod`, means all three always
 *  agree, instead of "08" (never lowered, still in the file's raw categories)
 *  and "8" (read back from an executed `continuous` leaf) silently becoming
 *  two different-looking selections for what the Lib already treats as one
 *  value. */
function canonicalPeriodValue(v: string): string {
  const n = Number(v);
  return v.trim() === '' || !Number.isFinite(n) ? v : String(n);
}

/** This leaf's value as a string, whichever shape it's in — canonicalized so
 *  a `categorical` leaf's raw string and a lowered `continuous` leaf's
 *  stringified min/max compare equal regardless of source formatting. */
function periodLeafValue(f: PeriodLeaf): string {
  return canonicalPeriodValue(f.type === 'categorical' ? f.category_value : String(f.min_limit_value));
}

/** The period column's position in this model's `columns[]`, or undefined if
 *  none picked yet (every period leaf shares the same column). */
export function periodColumnPosition(
  filters: FilterSpec[] | undefined,
  modelIndex: number
): number | undefined {
  const leaf = periodLeaves(filters, modelIndex)[0];
  return leaf ? periodLeafColumn(leaf) : undefined;
}

/** The period values currently selected for this model. */
export function selectedPeriods(filters: FilterSpec[] | undefined, modelIndex: number): string[] {
  return periodLeaves(filters, modelIndex).map(periodLeafValue);
}

/** The id of the filter card that actually represents this model's period
 *  selection right now — the OR-group's id once ≥2 values are picked, or the
 *  lone leaf's own id below that (withAutoCombined collapses the OR-group
 *  away below 2, filterSpec.ts). Undefined until a first value is ticked —
 *  there is nothing "created" yet. Lets the picker show the user which wire
 *  filter their pick just became, in place. */
export function activePeriodFilterId(
  filters: FilterSpec[] | undefined,
  modelIndex: number
): string | undefined {
  const leaves = periodLeaves(filters, modelIndex);
  if (leaves.length >= 2) return periodOrFilterId(modelIndex);
  if (leaves.length === 1) return leaves[0].id;
  return undefined;
}

/** Distinct values to offer for period selection at the given per-model
 *  column positions — union across every model that has one picked, mirroring
 *  `selectedCategories`'s union-across-files semantics (a period may appear in
 *  either file's capture). Empty ⇒ no column picked yet, or the file predates
 *  category capture. */
export function periodValueOptions(
  modelColumns: readonly { index: number }[][],
  columnPositionByModel: readonly (number | undefined)[],
  fileColsFor: (modelIdx: number) => FileColumn[]
): string[] {
  const set = new Set<string>();
  columnPositionByModel.forEach((pos, m) => {
    if (pos == null) return;
    const csv = modelColumns[m]?.[pos]?.index;
    if (csv == null) return;
    fileColsFor(m)
      .find((c) => c.index === csv)
      ?.categories?.forEach((v) => set.add(canonicalPeriodValue(v)));
  });
  return [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

/** Drop this model's period selection (e.g. re-pointing the period column —
 *  a value from the old column means nothing in the new one, same rationale
 *  as re-pointing any other categorical filter's column). */
export function clearPeriodSelection(
  filters: FilterSpec[] | undefined,
  modelIndex: number
): FilterSpec[] {
  const kept = (filters ?? []).filter(
    (f) => f.id !== periodOrFilterId(modelIndex) && !isPeriodLeafId(f.id, modelIndex)
  );
  return withAutoCombined(kept, modelIndex);
}

/** Toggle one period value on/off for this model, at the given column
 *  position (the model's own resolved position for the shared period column). */
export function togglePeriod(
  filters: FilterSpec[] | undefined,
  modelIndex: number,
  columnPos: number,
  value: string,
  on: boolean
): FilterSpec[] {
  const all = filters ?? [];
  const others = all.filter(
    (f) => f.id !== periodOrFilterId(modelIndex) && !isPeriodLeafId(f.id, modelIndex)
  );
  const current = periodLeaves(all, modelIndex);
  // Compare against the canonicalized incoming value (see `canonicalPeriodValue`)
  // so a checklist entry that round-tripped through a numeric `continuous`
  // lowering (e.g. "08" stored as "8") is recognized as already present on the
  // add path, and actually removed — rather than orphaned — on the remove path.
  const canonicalValue = canonicalPeriodValue(value);
  const next = on
    ? current.some((f) => periodLeafValue(f) === canonicalValue)
      ? current
      : [
          ...current,
          {
            id: nextPeriodLeafId(all, modelIndex),
            type: 'categorical' as const,
            category_column: columnPos,
            category_value: value,
          },
        ]
    : current.filter((f) => periodLeafValue(f) !== canonicalValue);
  return withAutoCombined([...others, ...next], modelIndex);
}
